import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Camera, ScanSearch, Share2, Plus, Sparkles } from "lucide-react";

const STEPS = [
  { icon: Camera, text: "Photograph your place with guided capture" },
  { icon: ScanSearch, text: "AI documents condition and flags issues" },
  { icon: Share2, text: "Share tamper-evident reports and, if needed, generate dispute letters" },
];

// Shown on the dashboard when the user has no real property yet: one clean card
// with the 3-step overview and two side-by-side actions (start / explore sample).
export function EmptyStartCard({ onSampleLoaded }) {
  const navigate = useNavigate();

  return (
    <div data-testid="empty-start-card" className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8">
      <h2 className="font-heading text-2xl font-bold tracking-tight text-navy">Document your rental — here's how</h2>
      <ol className="mt-5 space-y-3">
        {STEPS.map((s, i) => (
          <li key={s.text} className="flex items-start gap-3">
            <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand/10 text-brand shrink-0 font-bold text-sm">{i + 1}</span>
            <span className="pt-1 text-[15px] text-[#334155] leading-snug"><s.icon size={15} className="inline mr-1.5 -mt-0.5 text-[#94A3B8]" />{s.text}</span>
          </li>
        ))}
      </ol>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Button
          data-testid="quick-start-cta"
          onClick={() => navigate("/quick-start")}
          className="h-12 px-6 rounded-xl bg-brand hover:bg-brand-hover text-white w-full sm:w-auto"
        >
          <Camera size={17} className="mr-1.5" /> Seal your first room in 5 minutes
        </Button>
        <Button
          data-testid="start-documenting-btn"
          onClick={() => navigate("/start")}
          variant="outline"
          className="h-12 px-6 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC] w-full sm:w-auto"
        >
          <Plus size={17} className="mr-1.5" /> Full property setup
        </Button>
      </div>
      <p className="mt-2.5 text-sm text-[#64748B]">Start with one room. Add three photos. Create your first timestamped rental record.</p>

      <div className="mt-6 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 sm:p-6" data-testid="sample-discovery-block">
        <h3 className="font-heading text-xl font-bold tracking-tight text-navy">See a fully-documented example</h3>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed">
          Explore a read-only sample rental with Evidence Coverage, Proof Replay, Pre-Move-Out Self-Audit, Charge Storyboard and Exhibit Dossier. Nothing is saved to your account.
        </p>
        <Button
          data-testid="explore-sample-btn"
          onClick={() => navigate("/demo")}
          className="mt-4 h-12 px-6 rounded-xl bg-navy hover:bg-navy-hover text-white w-full sm:w-auto"
        >
          <Sparkles size={17} className="mr-2" /> Explore a sample property
        </Button>
      </div>
    </div>
  );
}
